// THE PLACE A MEMBER CAN SEE, REPLACE AND TURN OFF THEIR CALENDAR LINK
// (bead hf-7o6, answering the last acceptance criterion of hf-myz).
//
// The CAL-007 ruling accepted a non-expiring, unauthenticated bearer URL on the
// member's behalf. Rotation and revocation are the WHOLE of the control for that risk,
// so this surface is not a settings nicety — it is the control itself. hf-myz put it
// in one line: a credential nobody can see is a credential nobody rotates.
//
// Three decisions worth defending:
//
// NO `StateChip` FOR ON/OFF. The chip's states are the product's five need states and
// its colours are the tokens for them; "covered green" for a live link would borrow a
// meaning this has nothing to do with. On and off are said in words instead.
//
// THE LINK SITS IN A READ-ONLY FIELD, not a paragraph. A member has to get it into
// another app, so it needs to be focusable, selectable and labelled for a screen
// reader. There is no copy button: `navigator.clipboard` is unavailable in enough real
// contexts (insecure origins, older mobile browsers, permission prompts) that a button
// which silently does nothing would be worse than a field that always works.
//
// DESTRUCTIVE THINGS ASK FIRST, AND THE QUESTION NAMES THE CONSEQUENCE. Replacing kills
// a link a calendar is using; turning off stops the calendar updating. Both go through
// a dialog, and neither dialog says "are you sure" — it says what will happen.

import { Button, Dialog, EmptyState, StatusMessage, TextInput } from "@herefor/design-system";
import { CALENDAR_FEED_MESSAGES } from "@herefor/product-language";
import { useState, type ReactNode } from "react";

import type { MyFeedResult } from "../../convex/calendarFeed";
import { fetchAge, messageFor } from "./feedCopy";
import type { CalendarFeedController, FeedTitleMode } from "./useCalendarFeed";

export interface CalendarFeedSettingsProps {
  /** This member's feed, straight from `myFeed`. `null` means they have never made one. */
  readonly feed: MyFeedResult;
  readonly controller: CalendarFeedController;
  /**
   * "Now", injected rather than read.
   *
   * The liveness sentence is a function of the clock, and a component that reads the
   * clock itself renders something a test cannot assert on without freezing time.
   */
  readonly now: number;
  /** Already formatted by the caller — product-language never formats a date. */
  readonly createdOnLabel: string;
  readonly actionsDisabled?: boolean;
  /** Standalone by default; level two when composed into the settings page. */
  readonly headingLevel?: 1 | 2;
}

type Confirmation = "replace" | "turnOff" | null;

export function CalendarFeedSettings({
  feed,
  controller,
  now,
  createdOnLabel,
  actionsDisabled = false,
  headingLevel = 1,
}: CalendarFeedSettingsProps) {
  const [confirming, setConfirming] = useState<Confirmation>(null);
  const { state } = controller;

  const live = feed !== null && feed.state === "active";
  const busy = actionsDisabled || state.status === "working";
  const Heading = headingLevel === 1 ? "h1" : "h2";

  const close = () => {
    setConfirming(null);
  };

  const confirmAnd = (run: () => Promise<void>) => () => {
    setConfirming(null);
    void run();
  };

  return (
    <section className="feed-settings" data-feed-settings={live ? "live" : "off"}>
      <Heading>{CALENDAR_FEED_MESSAGES.heading}</Heading>
      <p className="feed-settings__lede">{CALENDAR_FEED_MESSAGES.explanation}</p>

      {live ? (
        <LiveFeed
          busy={busy}
          createdOnLabel={createdOnLabel}
          lastServedAt={feed.lastServedAt}
          now={now}
          onChooseTitleMode={(mode) => {
            void controller.chooseTitleMode(mode);
          }}
          onReplace={() => {
            setConfirming("replace");
          }}
          onTurnOff={() => {
            setConfirming("turnOff");
          }}
          titleMode={feed.titleMode}
        />
      ) : (
        <EmptyState
          action={
            <Button
              disabled={busy}
              onClick={() => {
                void controller.create();
              }}
              variant="primary"
            >
              {CALENDAR_FEED_MESSAGES.none.action}
            </Button>
          }
          description={CALENDAR_FEED_MESSAGES.none.body}
          title={CALENDAR_FEED_MESSAGES.none.title}
        />
      )}

      <Outcome controller={controller} />

      <Dialog
        footer={
          <>
            <Button onClick={close} variant="secondary">
              {CALENDAR_FEED_MESSAGES.replaceConfirm.cancel}
            </Button>
            <Button disabled={busy} onClick={confirmAnd(controller.replace)} variant="danger">
              {CALENDAR_FEED_MESSAGES.replaceConfirm.action}
            </Button>
          </>
        }
        onClose={close}
        open={confirming === "replace"}
        title={CALENDAR_FEED_MESSAGES.replaceConfirm.title}
      >
        <p>{CALENDAR_FEED_MESSAGES.replaceConfirm.body}</p>
      </Dialog>

      <Dialog
        footer={
          <>
            <Button onClick={close} variant="secondary">
              {CALENDAR_FEED_MESSAGES.turnOffConfirm.cancel}
            </Button>
            <Button disabled={busy} onClick={confirmAnd(controller.turnOff)} variant="danger">
              {CALENDAR_FEED_MESSAGES.turnOffConfirm.action}
            </Button>
          </>
        }
        onClose={close}
        open={confirming === "turnOff"}
        title={CALENDAR_FEED_MESSAGES.turnOffConfirm.title}
      >
        <p>{CALENDAR_FEED_MESSAGES.turnOffConfirm.body}</p>
      </Dialog>
    </section>
  );
}

function LiveFeed({
  busy,
  createdOnLabel,
  lastServedAt,
  now,
  onChooseTitleMode,
  onReplace,
  onTurnOff,
  titleMode,
}: {
  readonly busy: boolean;
  readonly createdOnLabel: string;
  readonly lastServedAt: number | undefined;
  readonly now: number;
  readonly onChooseTitleMode: (mode: FeedTitleMode) => void;
  readonly onReplace: () => void;
  readonly onTurnOff: () => void;
  readonly titleMode: FeedTitleMode;
}) {
  const age = fetchAge(lastServedAt, now);

  return (
    <div className="feed-settings__live">
      <p className="feed-settings__state">
        {CALENDAR_FEED_MESSAGES.liveLabel} · {createdOnLabel}
      </p>

      <p className="feed-settings__liveness" data-feed-liveness={age.unit}>
        {age.unit === "never" ? CALENDAR_FEED_MESSAGES.lastFetched.never : null}
        {age.unit === "withinHour" ? CALENDAR_FEED_MESSAGES.lastFetched.withinHour : null}
        {age.unit === "hours" ? CALENDAR_FEED_MESSAGES.lastFetched.hoursAgo(age.value) : null}
        {age.unit === "days" ? CALENDAR_FEED_MESSAGES.lastFetched.daysAgo(age.value) : null}
      </p>
      {/* The sentence that makes the liveness line actionable rather than trivia. */}
      {age.unit === "never" ? null : (
        <p className="feed-settings__hint">{CALENDAR_FEED_MESSAGES.lastFetched.unexpected}</p>
      )}

      <fieldset className="feed-settings__detail">
        <legend>{CALENDAR_FEED_MESSAGES.titleMode.legend}</legend>
        {(
          [
            ["discreet", CALENDAR_FEED_MESSAGES.titleMode.discreetLabel],
            ["detailed", CALENDAR_FEED_MESSAGES.titleMode.detailedLabel],
          ] as const
        ).map(([mode, label]) => (
          <label className="feed-settings__choice" key={mode}>
            <input
              checked={titleMode === mode}
              disabled={busy}
              name="feed-title-mode"
              onChange={() => {
                onChooseTitleMode(mode);
              }}
              type="radio"
              value={mode}
            />
            <span>{label}</span>
          </label>
        ))}
        <p className="feed-settings__hint">
          {titleMode === "detailed"
            ? CALENDAR_FEED_MESSAGES.titleMode.detailedHint
            : CALENDAR_FEED_MESSAGES.titleMode.discreetHint}
        </p>
        {/* Where the accepted vendor-caching risk actually reaches the person who
            carries it — beside the choice, not in a policy page. */}
        <p className="feed-settings__hint">{CALENDAR_FEED_MESSAGES.titleMode.cachingNote}</p>
      </fieldset>

      <div className="feed-settings__actions">
        <p className="feed-settings__hint">{CALENDAR_FEED_MESSAGES.replace.body}</p>
        <Button disabled={busy} onClick={onReplace} variant="secondary">
          {CALENDAR_FEED_MESSAGES.replace.action}
        </Button>

        <p className="feed-settings__hint">{CALENDAR_FEED_MESSAGES.turnOff.body}</p>
        <Button disabled={busy} onClick={onTurnOff} variant="danger">
          {CALENDAR_FEED_MESSAGES.turnOff.action}
        </Button>
      </div>
    </div>
  );
}

/**
 * What just happened.
 *
 * `tone="info"` throughout, including for a refusal. Nothing on this surface is an
 * emergency, and `tone="error"` sets `role="alert"`, which would cut across a screen
 * reader to announce that a settings change did not save.
 */
function Outcome({ controller }: { readonly controller: CalendarFeedController }): ReactNode {
  const { state } = controller;

  if (state.status === "idle" || state.status === "working") return null;

  if (state.status === "revealed") {
    return (
      <div className="feed-settings__outcome" data-feed-outcome="revealed">
        <StatusMessage tone="info">
          <div className="feed-settings__lines">
            <p className="feed-settings__outcome-title">
              {state.replaced
                ? CALENDAR_FEED_MESSAGES.replaced.title
                : CALENDAR_FEED_MESSAGES.reveal.title}
            </p>
            <p>
              {state.replaced
                ? CALENDAR_FEED_MESSAGES.replaced.body
                : CALENDAR_FEED_MESSAGES.reveal.body}
            </p>

            <TextInput
              label={CALENDAR_FEED_MESSAGES.linkLabel}
              onFocus={(event) => {
                event.currentTarget.select();
              }}
              readOnly
              value={state.link}
            />

            <p>{CALENDAR_FEED_MESSAGES.whoCanSee}</p>
            <Button onClick={controller.dismiss} variant="secondary">
              {CALENDAR_FEED_MESSAGES.reveal.action}
            </Button>
          </div>
        </StatusMessage>
      </div>
    );
  }

  if (state.status === "alreadyCreated") {
    return (
      <div className="feed-settings__outcome" data-feed-outcome="already-created">
        <StatusMessage tone="info">
          <div className="feed-settings__lines">
            <p className="feed-settings__outcome-title">
              {CALENDAR_FEED_MESSAGES.alreadyCreated.title}
            </p>
            <p>{CALENDAR_FEED_MESSAGES.alreadyCreated.body}</p>
            <Button
              onClick={() => {
                void controller.replace();
              }}
              variant="secondary"
            >
              {CALENDAR_FEED_MESSAGES.alreadyCreated.action}
            </Button>
          </div>
        </StatusMessage>
      </div>
    );
  }

  if (state.status === "turnedOff") {
    return (
      <div className="feed-settings__outcome" data-feed-outcome="turned-off">
        <StatusMessage tone="info">
          <div className="feed-settings__lines">
            <p className="feed-settings__outcome-title">{CALENDAR_FEED_MESSAGES.turnedOff.title}</p>
            <p>{CALENDAR_FEED_MESSAGES.turnedOff.body}</p>
          </div>
        </StatusMessage>
      </div>
    );
  }

  const message = messageFor(state.rejection);

  return (
    <div
      className="feed-settings__outcome"
      data-feed-outcome="refused"
      data-feed-rejection={state.rejection.kind}
    >
      <StatusMessage tone="info">
        <div className="feed-settings__lines">
          <p className="feed-settings__outcome-title">{message.title}</p>
          <p>{message.body}</p>
        </div>
      </StatusMessage>
    </div>
  );
}
